/**
 * System Service — the engine's System Health endpoints (/api/system/*).
 * The engine, not Supabase: Supabase may be the thing that is down. Super-admin only.
 */
import { aiClient } from './client';
import { config } from '../config';

/** How long the reachability check waits before calling the engine unreachable (a busy pod can be slow). */
const ENGINE_PING_TIMEOUT_MS = 10000;

/** One service's state (engine system_health.ComponentStatus). */
export const ComponentStatus = {
  OK: 'ok',
  SLOW: 'slow',
  DEGRADED: 'degraded',
  DOWN: 'down',
  UNKNOWN: 'unknown',
} as const;
export type ComponentStatus = (typeof ComponentStatus)[keyof typeof ComponentStatus];

/** The whole deployment's state (engine system_health.OverallStatus). */
export const OverallStatus = {
  OPERATIONAL: 'operational',
  DEGRADED: 'degraded',
  UNAVAILABLE: 'unavailable',
} as const;
export type OverallStatus = (typeof OverallStatus)[keyof typeof OverallStatus];

export interface HealthComponent {
  id: string;
  name: string;
  group: string;
  provider: string;
  critical: boolean;
  impact: string;
  status: ComponentStatus;
  detail?: string | null;
  latency_ms?: number | null;
  checked_at: string;
  since: string;
  /** Judged from real traffic, never actively checked (client models on their own key). */
  passive: boolean;
  tenant_id?: string | null;
}

export interface SystemHealth {
  overall: OverallStatus;
  can_query: boolean;
  message: string;
  checked_at: string;
  ttl_seconds: number;
  components: HealthComponent[];
}

export const systemService = {
  getHealth: async (): Promise<SystemHealth> => (await aiClient.get<SystemHealth>('/api/system/health')).data,

  /** "Test now" — super-admin only; the engine rate-limits it. */
  refresh: async (): Promise<SystemHealth> => (await aiClient.post<SystemHealth>('/api/system/health/refresh')).data,

  /**
   * Is the AI engine answering at all? (the GPU pod may be stopped). Plain fetch on purpose: no auth,
   * no retries and no error reporting, so polling a stopped pod stays silent. Only the engine's own
   * `/health` answer counts — a proxy error page from the host does not.
   */
  isEngineReachable: async (): Promise<boolean> => {
    try {
      // Never served from a cache: a proxy/browser-cached answer from while the pod was booting would
      // keep reporting "down" after the engine is up. `no-store` + a unique query string rule that out.
      const res = await fetch(`${config.ai.apiUrl}/health?_=${Date.now()}`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(ENGINE_PING_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      const body = await res.json().catch(() => null);
      return body?.status === 'healthy';
    } catch {
      return false;
    }
  },
};
